import axios from 'axios';

/**
 * Team Tracking Service
 * Handles API calls for the team tracking dashboard
 * Uses separate authentication from main app (tracking admin auth)
 */
class TrackingService {
  constructor() {
    const port = process.env.REACT_APP_API_PORT || '8003';
    this.baseURL = process.env.REACT_APP_API_URL || `http://localhost:${port}/api`;

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor for tracking auth
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('tracking_admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear tracking admin token on 401
          localStorage.removeItem('tracking_admin_token');
          localStorage.removeItem('tracking_admin');
          // Dispatch event for components to handle
          window.dispatchEvent(new CustomEvent('tracking-auth-expired'));
        }
        return Promise.reject(error);
      }
    );
  }

  // ============ Authentication ============

  /**
   * Admin login
   */
  async login(email, password) {
    const response = await this.api.post('/tracking/admin/login', { email, password });
    if (response.data.success && response.data.token) {
      localStorage.setItem('tracking_admin_token', response.data.token);
      localStorage.setItem('tracking_admin', JSON.stringify(response.data.admin));
    }
    return response.data;
  }

  /**
   * Admin logout
   */
  logout() {
    localStorage.removeItem('tracking_admin_token');
    localStorage.removeItem('tracking_admin');
  }

  /**
   * Check if admin is logged in
   */
  isAuthenticated() {
    return !!localStorage.getItem('tracking_admin_token');
  }

  /**
   * Get current admin
   */
  getCurrentAdmin() {
    const admin = localStorage.getItem('tracking_admin');
    return admin ? JSON.parse(admin) : null;
  }

  /**
   * Setup first admin (only works if no admin exists)
   */
  async setupFirstAdmin(email, name, password) {
    const response = await this.api.post('/tracking/setup-first-admin', {
      email,
      name,
      password
    });
    return response.data;
  }

  /**
   * Create new admin (requires auth)
   */
  async createAdmin(email, name, password) {
    const response = await this.api.post('/tracking/admin/create', {
      email,
      name,
      password
    });
    return response.data;
  }

  /**
   * Get all admins (requires auth)
   */
  async getAdmins() {
    const response = await this.api.get('/tracking/admin/admins');
    return response.data;
  }

  // ============ Dashboard Stats ============

  /**
   * Get dashboard statistics
   */
  async getStats() {
    const response = await this.api.get('/tracking/admin/stats');
    return response.data;
  }

  // ============ Users ============

  /**
   * Get all users
   */
  async getUsers() {
    const response = await this.api.get('/tracking/admin/users');
    return response.data;
  }

  /**
   * Get user details
   */
  async getUser(email) {
    const response = await this.api.get(`/tracking/admin/users/${encodeURIComponent(email)}`);
    return response.data;
  }

  /**
   * Block a user
   */
  async blockUser(email, reason) {
    const response = await this.api.post(
      `/tracking/admin/users/${encodeURIComponent(email)}/block`,
      null,
      { params: { reason } }
    );
    return response.data;
  }

  /**
   * Unblock a user
   */
  async unblockUser(email) {
    const response = await this.api.post(
      `/tracking/admin/users/${encodeURIComponent(email)}/unblock`
    );
    return response.data;
  }

  // ============ Sessions ============

  /**
   * Get sessions (optionally filtered by user)
   */
  async getSessions(userEmail = null, limit = 50) {
    const params = { limit };
    if (userEmail) {
      params.user_email = userEmail;
    }
    const response = await this.api.get('/tracking/admin/sessions', { params });
    return response.data;
  }

  /**
   * Get session details
   */
  async getSession(sessionId) {
    const response = await this.api.get(`/tracking/admin/sessions/${sessionId}`);
    return response.data;
  }

  /**
   * Get events for a session
   */
  async getSessionEvents(sessionId) {
    const response = await this.api.get(`/tracking/admin/sessions/${sessionId}/events`);
    return response.data;
  }

  // ============ Events ============

  /**
   * Get events (optionally filtered)
   */
  async getEvents(limit = 100, userEmail = null, eventType = null) {
    const params = { limit };
    if (userEmail) params.user_email = userEmail;
    if (eventType) params.event_type = eventType;
    const response = await this.api.get('/tracking/admin/events', { params });
    return response.data;
  }

  // ============ Server Commands ============

  /**
   * Create a server command
   */
  async createCommand(targetType, targetValue, commandType, message, payload = null, expiresInHours = 24) {
    const response = await this.api.post('/tracking/admin/commands', {
      target_type: targetType,
      target_value: targetValue,
      command_type: commandType,
      message,
      payload,
      expires_in_hours: expiresInHours
    });
    return response.data;
  }

  /**
   * Broadcast message to all users (legacy - tracking system)
   */
  async broadcastMessage(message, expiresInHours = 24) {
    const response = await this.api.post('/tracking/admin/broadcast', null, {
      params: { message, expires_in_hours: expiresInHours }
    });
    return response.data;
  }

  /**
   * Create broadcast message (new MongoDB-based system for status line)
   */
  async createBroadcast(message, messageType = 'announcement', priority = 'normal', emoji = '📢', endHours = 24) {
    const response = await this.api.post('/broadcast/messages', {
      message,
      message_type: messageType,
      priority,
      emoji,
      target_audience: 'all',
      end_date: new Date(Date.now() + endHours * 60 * 60 * 1000).toISOString()
    });
    return response.data;
  }

  /**
   * Get all broadcast messages
   */
  async getBroadcasts(messageType = null, isActive = null) {
    const params = {};
    if (messageType) params.message_type = messageType;
    if (isActive !== null) params.is_active = isActive;
    const response = await this.api.get('/broadcast/messages', { params });
    return response.data;
  }

  /**
   * Delete broadcast message
   */
  async deleteBroadcast(messageId) {
    const response = await this.api.delete(`/broadcast/messages/${messageId}`);
    return response.data;
  }

  /**
   * Quick hackathon broadcast
   */
  async quickHackathonBroadcast(message, endHours = 24) {
    const response = await this.api.post(`/broadcast/quick/hackathon?message=${encodeURIComponent(message)}&end_hours=${endHours}`);
    return response.data;
  }

  // ============ Alerts ============

  /**
   * Get pending tampering alerts
   */
  async getAlerts() {
    const response = await this.api.get('/tracking/admin/alerts');
    return response.data;
  }

  /**
   * Resolve an alert
   */
  async resolveAlert(alertId) {
    const response = await this.api.post(`/tracking/admin/alerts/${alertId}/resolve`);
    return response.data;
  }

  // ============ Setup ============

  /**
   * Initialize tracking database
   */
  async initDatabase() {
    const response = await this.api.post('/tracking/init-db');
    return response.data;
  }

  // ============ Claude Code Usage ============

  /**
   * Get Claude Code usage for a specific date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {boolean} forceRefresh - Force refresh from API (bypass cache)
   */
  async getClaudeUsage(date, forceRefresh = false) {
    const response = await this.api.get('/tracking/admin/claude-usage', {
      params: { date, force_refresh: forceRefresh }
    });
    return response.data;
  }

  /**
   * Get Claude Code usage for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @param {boolean} forceRefresh - Force refresh from API
   */
  async getClaudeUsageRange(startDate, endDate, forceRefresh = false) {
    const response = await this.api.get('/tracking/admin/claude-usage/range', {
      params: { start_date: startDate, end_date: endDate, force_refresh: forceRefresh }
    });
    return response.data;
  }

  /**
   * Get Claude Code usage summary for the last 7 days
   * @param {boolean} forceRefresh - Force refresh from API
   */
  async getClaudeUsageWeekly(forceRefresh = false) {
    const response = await this.api.get('/tracking/admin/claude-usage/weekly', {
      params: { force_refresh: forceRefresh }
    });
    return response.data;
  }

  /**
   * Test if an Anthropic Admin API key is valid
   * @param {string} apiKey - The API key to test
   */
  async testClaudeApiKey(apiKey) {
    const response = await this.api.post('/tracking/admin/claude-usage/test-api-key', null, {
      params: { api_key: apiKey }
    });
    return response.data;
  }

  /**
   * Clear Claude usage cache
   * @param {string|null} date - Specific date to clear, or null for all
   */
  async clearClaudeUsageCache(date = null) {
    const response = await this.api.post('/tracking/admin/claude-usage/clear-cache', null, {
      params: date ? { date } : {}
    });
    return response.data;
  }

  // ============ Team Logs ============

  /**
   * Get team logs with events grouped by user
   * @param {number} hours - Get logs from last N hours (default 24)
   * @param {string} project - Optional project path filter
   */
  async getTeamLogs(hours = 24, project = null) {
    const params = { hours };
    if (project) params.project = project;
    const response = await this.api.get('/tracking/admin/team-logs', { params });
    return response.data;
  }

  /**
   * Get hackathon team logs with events grouped by hackathon teams
   * @param {number} hours - Get logs from last N hours (default 48)
   * @param {string} eventId - Optional hackathon event ID filter
   */
  async getHackathonTeamLogs(hours = 48, eventId = null) {
    const params = { hours };
    if (eventId) params.event_id = eventId;
    const response = await this.api.get('/tracking/admin/hackathon-team-logs', { params });
    return response.data;
  }

  // ============ Hackathon Schedule ============

  /**
   * Get hackathon schedule and current IST time
   */
  async getHackathonSchedule() {
    // Public endpoint - no auth needed
    const response = await axios.get(`${this.baseURL}/broadcast/public/hackathon/schedule`);
    return response.data;
  }

  /**
   * Get current hackathon broadcast message
   */
  async getHackathonBroadcast() {
    // Public endpoint - no auth needed
    const response = await axios.get(`${this.baseURL}/broadcast/public/hackathon`);
    return response.data;
  }

  /**
   * Get status line message (for preview)
   */
  async getStatuslineMessage() {
    // Public endpoint - no auth needed
    const response = await axios.get(`${this.baseURL}/broadcast/public/statusline`);
    return response.data;
  }
}

// Export singleton instance
const trackingService = new TrackingService();
export default trackingService;
